import React from "react";
import HeroContent from "@/components/hero/HeroContent";
import HeroImage from "@/components/hero/HeroImage";
import HeroLogo from "@/components/hero/HeroLogo";
import HeroQuote from "./hero/HeroQuote";
import HeroQuoteAfterImage from "./hero/HeroQuoteAfterImage";

interface HeroProps {
  title: string;
  subtitle: string;
  description: string;
  primaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  secondaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  backgroundClass?: string;
  imageUrl?: string;
  isHomepage?: boolean;
  pageName?: string;
  hideHeaderLogo?: boolean;
  hideHeroLogo?: boolean;
  logoPosition?: "top-left" | "top-center";
  portraitMode?: boolean;
  slogan?: string;
  highlightedStatement?: string;
  highlightedStatementClass?: string;
  showHighlightedStatementAfterImage?: boolean;
  imageObjectPosition?: string;
  headingLevel?: 'h1' | 'h2' | 'h3';
}

const HeroSection = React.forwardRef<HTMLElement, HeroProps>(({
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  backgroundClass = "bg-white",
  imageUrl,
  isHomepage = false,
  pageName = "",
  hideHeaderLogo = false,
  hideHeroLogo = false,
  logoPosition = "top-center",
  portraitMode = false,
  slogan,
  highlightedStatement,
  highlightedStatementClass,
  showHighlightedStatementAfterImage = false,
  imageObjectPosition,
  headingLevel = 'h1'
}, ref) => {
  const needsTransition = backgroundClass !== "bg-white";
  // Determine if text needs extra readability enhancements based on background
  const textEnhancementClass = "";

  // Apply the home-hero-professional class specifically for the homepage
  // This ensures we have a warm, professional color scheme
  const finalBackgroundClass = isHomepage 
    ? "home-hero-professional" 
    : backgroundClass;

  return (
    <section
      ref={ref}
      className={`hero-spacing ${finalBackgroundClass} relative overflow-hidden`}
    >
      
      {needsTransition && (
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/20 to-transparent pointer-events-none"></div>
      )}
      
      <div className="container px-4 mx-auto relative z-10">
        {!hideHeroLogo && !hideHeaderLogo && logoPosition === "top-center" && (
          <HeroLogo />
        )}

        <div
          className={`${
            portraitMode
              ? "grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center"
              : `flex flex-col items-center justify-center text-center gap-6 lg:gap-10 ${
                  imageUrl && !isHomepage ? 'lg:grid lg:grid-cols-2' : ''
                }`
          }`}
        >
          {!hideHeroLogo && !hideHeaderLogo && logoPosition === "top-left" && (
            <div className="mb-8 lg:col-span-2 lg:mb-12">
              <HeroLogo />
            </div>
          )}

          {!showHighlightedStatementAfterImage && highlightedStatement && (
            <HeroQuote 
              statement={highlightedStatement} 
              className={`${highlightedStatementClass} text-left`}
            />
          )}

          {imageUrl && isHomepage && (
            <div className="block lg:hidden w-full mb-8">
              <HeroImage
                imageUrl={imageUrl}
                alt={`${pageName} - Jörg Hörmann Coaching & Consulting`}
                portraitMode={portraitMode}
                imageObjectPosition={imageObjectPosition}
                isHomepage={isHomepage}
                loading="eager"
              />
            </div>
          )}

          <HeroContent
            title={title}
            subtitle={subtitle}
            description={description}
            primaryCTA={primaryCTA}
            secondaryCTA={secondaryCTA}
            isHomepage={isHomepage}
            portraitMode={portraitMode}
            slogan={slogan}
            textEnhancementClass={textEnhancementClass}
          />

          {imageUrl && (
            <div className={`${isHomepage ? 'hidden lg:block' : ''} ${portraitMode ? 'lg:col-span-5' : ''}`}>
              <HeroImage
                imageUrl={imageUrl}
                alt={`${pageName} - Jörg Hörmann Coaching & Consulting`}
                portraitMode={portraitMode}
                imageObjectPosition={imageObjectPosition}
                isHomepage={isHomepage}
                loading={isHomepage ? "eager" : "lazy"}
              />
            </div>
          )}

          {showHighlightedStatementAfterImage && highlightedStatement && (
            <HeroQuoteAfterImage 
              statement={highlightedStatement} 
              className={`${highlightedStatementClass} text-left`}
            />
          )}
        </div>
      </div>
    </section>
  );
});

HeroSection.displayName = "HeroSection";

export default HeroSection;
